import React, { useState } from 'react';
import { ArrowLeft, Camera } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const TBSAAssessment = ({ onNext, onBack, onDataChange, onImageUpload, data, uploadedImages }) => {
  const [tbsa, setTbsa] = useState(data?.tbsa || '');
  const [burnDepth, setBurnDepth] = useState(data?.burnDepth || '');
  const [burnLocations, setBurnLocations] = useState(data?.burnLocations || '');
  const [localImages, setLocalImages] = useState(uploadedImages || []);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      // Convert files to base64 for storage
      const imagePromises = files.map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve({
              name: file.name,
              type: file.type,
              data: reader.result,
              timestamp: new Date().toISOString()
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const newImages = await Promise.all(imagePromises);
      setLocalImages(prev => [...prev, ...newImages]);
      
      if (onImageUpload) {
        onImageUpload(newImages);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Error uploading images. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleNext = () => {
    const assessmentData = { tbsa, burnDepth, burnLocations };
    if (onDataChange) {
      onDataChange({ tbsa_assessment: assessmentData });
    }
    onNext();
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-black py-4">
      <div className="w-full max-w-sm mx-auto px-4">
        <div className="flex flex-col min-h-screen bg-zinc-900 rounded-3xl border-2 border-orange-500/30 shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b-2 border-orange-500/30">
            <Button onClick={onBack} variant="ghost" className="flex items-center gap-2 text-orange-500 text-lg font-bold">
              <ArrowLeft className="w-6 h-6" /> Back
            </Button>
            <h1 className="text-lg font-black text-orange-500">TBSA Assessment</h1>
            <div className="w-20" />
          </div>

          <main className="flex-1 px-4 py-6 space-y-6 bg-black overflow-y-auto">
            {/* Important Notice */}
            <div className="p-4 rounded-2xl bg-zinc-900 border-2 border-orange-500/20">
              <p className="text-base text-orange-100 leading-relaxed">
                Remove all clothing and jewelry from the patient before proceeding.
              </p>
            </div>

            {/* Photograph Burn Areas */}
            <div>
              <h2 className="text-xl font-black text-orange-400 mb-3">Photograph Burn Areas</h2>
              <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-orange-500/50 p-6 text-center bg-zinc-900">
                <Camera className="w-12 h-12 text-orange-400" />
                <p className="font-bold text-lg text-orange-100">Add Photos</p>
                <p className="text-base text-orange-200">Capture burn areas for assessment</p>
                
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="camera-input"
                />
                <label
                  htmlFor="camera-input"
                  className="w-full bg-orange-500 text-white font-bold py-3 px-4 rounded-xl hover:bg-orange-600 transition-colors cursor-pointer text-center"
                >
                  {uploading ? 'Uploading...' : 'Take Photo / Upload'}
                </label>

                {/* Display uploaded images */}
                {localImages.length > 0 && (
                  <div className="w-full mt-3">
                    <p className="text-orange-300 text-sm mb-2 font-bold">{localImages.length} photo(s) uploaded</p>
                    <div className="grid grid-cols-3 gap-2">
                      {localImages.map((img, idx) => (
                        <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border-2 border-orange-500/50">
                          <img
                            src={img.data}
                            alt={`Burn ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Estimation Charts */}
            <div>
              <h2 className="text-xl font-black text-orange-400 mb-3">Estimate Burn Size</h2>
              <p className="text-base text-orange-200 mb-3">Use Rule of Nines or Lund-Browder chart</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative group">
                  <div className="absolute inset-0 bg-black/60 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <span className="text-orange-100 font-bold text-base">Rule of Nines</span>
                  </div>
                  <div 
                    className="w-full bg-center bg-no-repeat aspect-square bg-cover rounded-xl border-2 border-orange-500/30"
                    style={{ backgroundImage: "url('https://lh3.googleusercontent.com/aida-public/AB6AXuA38Bd5V3xJONhLTSLGL7NsaO_CZaXPXPcBpJylcpmAUHGAQB7dz7zp9vnvQoM4GVMAT-4u-sv5GJJWQ2TbKYbIWaqTuGpjfQ45EraKc4-EPwXNSLbwakDmsRHG3ojTjQ1ugVNvnKB-V_U9B5QNZ2XPcoeItFYkBM6m4wMVngz8GciXr6Vgs-E_AITQcAnuCColRiz4tV2WUnvB7VDhfibu_iZYSybb2Xi186XVSA4ZSAPM96c1mKVUz3j7tbaItqoMC4HaehKj3Ec')" }}
                  />
                </div>
                <div className="relative group">
                  <div className="absolute inset-0 bg-black/60 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <span className="text-orange-100 font-bold text-base">Lund-Browder</span>
                  </div>
                  <div 
                    className="w-full bg-center bg-no-repeat aspect-square bg-cover rounded-xl border-2 border-orange-500/30"
                    style={{ backgroundImage: "url('https://lh3.googleusercontent.com/aida-public/AB6AXuAkaMTjD1Qyy-e_PcZVG1R70WWhOX6tZ4HjAYqKLOSCvLQH-bT6P6PAfWo4rUId8tCMx6IQhk71_7ZYHNS73Tvq1dZVqzjMesrRgtaNm8GC4R-cvIihiKCkth1ZlV9vC1iVDoiwkWbck3WgN2RQAQjRxg5lB--qycMnDGnCZmKwyZL_gZywYUZ9L_2AW4fmq2x4NYDnBVusQgZzuN4tVMR-o5LDceg98U3IZ9QljJl3wiJ2wXiR2ioi5Ll455Iq1p29aYMsrfdIc0E')" }}
                  />
                </div>
              </div>
            </div>

            {/* Input Fields */}
            <div className="space-y-4">
              <div>
                <Label className="text-lg font-bold text-orange-400 mb-2 block">TBSA Burned (%)</Label>
                <Input
                  type="number"
                  placeholder="Enter TBSA %"
                  value={tbsa}
                  onChange={(e) => setTbsa(e.target.value)}
                  className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100 placeholder:text-zinc-500"
                />
              </div>
              <div>
                <Label className="text-lg font-bold text-orange-400 mb-2 block">Burn Depth</Label>
                <select
                  value={burnDepth}
                  onChange={(e) => setBurnDepth(e.target.value)}
                  className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100"
                >
                  <option value="">Select depth</option>
                  <option value="superficial">Superficial</option>
                  <option value="partial_thickness">Partial Thickness</option>
                  <option value="full_thickness">Full Thickness</option>
                </select>
              </div>
              <div>
                <Label className="text-lg font-bold text-orange-400 mb-2 block">Burn Locations</Label>
                <textarea
                  placeholder="Describe burn locations"
                  value={burnLocations}
                  onChange={(e) => setBurnLocations(e.target.value)}
                  rows="3"
                  className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100 placeholder:text-zinc-500"
                />
              </div>
            </div>
          </main>

          <div className="p-4 border-t-2 border-orange-500/30 bg-zinc-900">
            <Button onClick={handleNext} className="w-full h-14 bg-orange-500 text-white text-lg font-black rounded-xl hover:bg-orange-600 shadow-xl">
              Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TBSAAssessment;